import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/formatters";
import { ReceivedGift } from "@/hooks/useReceivedGifts";
import { Coins, TrendingUp, Calendar } from "lucide-react";

interface EarningsBreakdownProps {
  gifts: ReceivedGift[];
}

const EarningsBreakdown = ({ gifts }: EarningsBreakdownProps) => {
  const completedGifts = gifts.filter(g => g.status === "succeeded");
  const totalEarnings = completedGifts.reduce((sum, gift) => sum + (gift.net_amount || 0), 0);
  const totalGross = completedGifts.reduce((sum, gift) => sum + gift.amount, 0);
  const averageGift = completedGifts.length > 0 ? totalEarnings / completedGifts.length : 0;
console.log('[EarningsBreakdown] completedGifts', completedGifts);
  // Group by month
  const earningsByMonth = completedGifts.reduce((acc, gift) => {
    const month = new Date(gift.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    acc[month] = (acc[month] || 0) + (gift.net_amount || 0);
    return acc;
  }, {} as Record<string, number>);

  const monthlyData = Object.entries(earningsByMonth)
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .slice(-6); // Last 6 months

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Coins className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground">Total Net</span>
            </div>
            <div className="text-xl font-bold text-primary">
              {formatCurrency(totalEarnings, completedGifts[0]?.currency || "gbp")}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-xs text-muted-foreground">Avg Gift</span>
            </div>
            <div className="text-xl font-bold text-green-600">
              {formatCurrency(Math.round(averageGift), completedGifts[0]?.currency || "gbp")}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Last 6 Months
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {monthlyData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No earnings data yet
            </p>
          ) : (
            monthlyData.map(([month, amount]) => (
              <div key={month} className="flex items-center justify-between p-2 rounded bg-muted/30">
                <span className="text-sm font-medium">{month}</span>
                <span className="text-sm font-bold text-primary">
                  {formatCurrency(amount, completedGifts[0]?.currency || "gbp")}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <div className="p-4 rounded-lg bg-muted/30 border border-border/30">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-muted-foreground">Total Gross</span>
          <span className="font-semibold">
            {formatCurrency(totalGross, completedGifts[0]?.currency || "gbp")}
          </span>
        </div>
        <div className="flex justify-between text-sm mb-1">
          <span className="text-muted-foreground">Platform Fees</span>
          <span className="font-semibold text-red-600">
            -{formatCurrency(totalGross - totalEarnings, completedGifts[0]?.currency || "gbp")}
          </span>
        </div>
        <div className="h-px bg-border my-2" />
        <div className="flex justify-between">
          <span className="font-bold">Your Earnings</span>
          <span className="font-bold text-primary">
            {formatCurrency(totalEarnings, completedGifts[0]?.currency || "gbp")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default EarningsBreakdown;
